import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import TitleItems from 'Src/components/TitleItems';
import { CardReaderDetails } from 'Src/feature/card-reader/CardReaderDetails';
import { CardReaderDisplayMessage } from 'Src/feature/card-reader/CardReaderDisplayMessage';
import { CardReaderPaymentMessage } from 'Src/feature/card-reader/CardReaderPaymentMessage';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { UsedModalSearchParamControl } from 'Src/feature/modal/useModalControl';

import { PayInManual } from './PayInManual';
import { Field, Form, usePayIn } from './usePayIn';

const sectionId = 'PayInView';

interface PayInProps {
  baseModalControl: UsedModalSearchParamControl;
}

const PayInView: FC<PayInProps> = ({
  baseModalControl,
}) => {
  const { t } = useTranslation();
  const store = usePayIn(baseModalControl);
  return (
    <>
      <Modal
        data-testid={sectionId}
        variant="fullScreen"
        open={store.modal.base.isOpen}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.onClose} />}>
          <ModalTitle copy={t('payInOut.labels.payIn')} />
        </ModalHeader>
        <ModalContent>
          <Form usedForm={store.form} omitChangeTracking>
            <Field
              type="currency"
              name="amount"
              label={t('payInOut.labels.payInAmount')}
              required
            />
            <Field
              data-testid={`${sectionId}-reason-toggle-list`}
              type="toggle"
              name="payInOutReasonId"
              variant="buttons"
              label={t('labels.whatIsItFor')}
              options={store.options.payInReasons ?? []}
              required
              // show an error message to user when pay in reason(s) not configured or empty
              // - "undefined" value will fall back to the internal validation mechanisms for this field
              error={!store.options.payInReasons?.length ? true : undefined}
              errorText={!store.options.payInReasons?.length ? t('payInOut.labels.payInReasonsNotAvailable') : undefined}
            />
            <Field
              type="text"
              name="notes"
              multiline
              maxRows={3}
              label={t('payInOut.labels.payInNotes')}
            />
            <Field
              type="select"
              variant="contained"
              fullWidth
              name="paymentMethod"
              label={t('payInOut.labels.paymentMethod')}
              options={store.options.payInOutPaymentMethods}
            />
            {store.options.cardReaderCCMethods.length > 1 && store.is.cardReader && (
              <Field
                type="select"
                variant="contained"
                fullWidth
                name="ccPaymentMethodId"
                label={t('payInOut.labels.ccPaymentMethod')}
                options={store.options.cardReaderCCMethods}
              />
            )}
            {store.options.otherPaymentMethods.length > 1 && store.is.cash && (
              <Field
                type="select"
                variant="contained"
                fullWidth
                name="cashPaymentMethodId"
                label={t('payInOut.labels.otherPaymentMethod')}
                options={store.options.otherPaymentMethods}
              />
            )}
          </Form>
        </ModalContent>
        <ModalActions>
          {store.is.manualCC
            ? (
              <PayInManual payInStore={store} />
            )
            : (
              <ModalAction
                data-testid={`${sectionId}-record-btn`}
                onClick={store.onRecordPayIn}
                color="primary"
                disabled={!store.canSubmit}
                label={store.is.cash ? t('payInOut.labels.payInRecord') : t('payInOut.labels.payInProcessAndRecord')}
              />
            )}
        </ModalActions>
      </Modal>

      <Modal
        data-testid={`${sectionId}-modal-saveConfirm`}
        open={store.modal.done.isOpen}
      >
        <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
          <Copy value={store.statusMessage} />
        </ModalContent>
        <ModalActions>
          <ModalAction onClick={store.onClose} color="primary" label={t('labels.done')} />
        </ModalActions>
      </Modal>

      <Modal
        data-testid={`${sectionId}-confirm-modal`}
        open={store.modal.confirm.isOpen}
        onBackDropClick={store.onClose}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.onClose} />}>
          <ModalTitle copy={t('payInOut.labels.payInConfirmTitle')} />
        </ModalHeader>
        <ModalContent>
          <TitleItems
            variant={['background', 'large']}
            justifyContent="space-between"
            items={[
              { primary: t('employee.label'), secondary: store.employeeFullName },
              { primary: t('payInOut.labels.payInAmount'), secondary: <Copy type="currency" value={store.formValues.amount} /> },
            ]}
          />
        </ModalContent>
        <ModalActions>
          <ModalAction color="secondary" onClick={store.onClose} label={t('labels.cancel')} />
          <ModalAction color="primary" onClick={store.onSave} label={t('payInOut.labels.confirmPayIn')} />
        </ModalActions>
      </Modal>

      <Modal
        data-testid={`${sectionId}-modal-cc-payment`}
        open={store.modal.card.isOpen}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.modal.card.close} />} >
          <ModalTitle copy={t('payInOut.labels.payInCardReaderOperation')} />
        </ModalHeader>
        <ModalContent flex={{ alignItems: 'center', gap: 'xs' }}>
          <CardReaderDetails
            justifyContent="center"
            reader={store.connectedCardReader}
            display={['connectedIcon', 'connectedCopy', 'batteryIcon', 'batteryPercent']}
          />
          <CardReaderPaymentMessage amount={store.formValues.amount} />
          <CardReaderDisplayMessage />
        </ModalContent>
      </Modal>
    </>
  );
};

export type { PayInProps };
export { PayInView, sectionId };
